import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { useFocusTrap } from 'utils/hooks/useFocusTrap';
import { useIntrinsicClick } from 'utils/hooks/useIntrinsicClick';
import { useKeyPress } from 'utils/hooks/useKeyPress';
import noop from 'utils/noop';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { CloneErrorBanner } from 'modules/workflowNodeEditors/components/CloneErrorBanner';
import { GhostButton } from 'ui/button';
import { Close24 } from 'ui/icons';
import { Spinner } from 'ui/spinner';
import { colors } from 'ui/theme';
import { DrawerHeader } from 'ui/workflows/drawer';
import { useCampaign } from 'modules/workflowNodeEditors/components/sendNodeEditors/BaseSendEditor/BaseSendEditor.hooks';
import { useJourneyReviewContext } from 'modules/workflows/context/JourneyReviewContext';
import { NODE_HAS_UNSAVED_CHANGES_MODAL } from '../../constants';
import {
  closeNode,
  deselectNode,
  fetchEventFieldMappingsRequest,
  getAreMappingsPopulated,
  getIsClonedNodeInvalid,
  getIsJourneyFieldMappingsEnabled,
  getIsJourneyReviewPanelEnabled,
  getIsNodeEditorAsyncLoading,
  getIsNodeEditorReadOnly,
  getNodeIsOpening,
  getWorkflowType,
  openModal,
  removeClonedJourneyErrorNode,
  removeNodeError,
  updateNode,
  WorkflowsRootState,
} from '../../ducks';
import { Node, NodeConfig, NodeData } from '../../types';
import { getBaseNodeConfig } from '../../utils';
import { EditableTileHeader } from '../EditableTextInput';
import { EditorNote, NoteButton } from '../NodeEditorNotes';
import { NodeEditorFooter } from './NodeEditorFooter';
import {
  HeaderNodeDetails,
  IconContainer,
  MainDrawer,
  NodeEditorWrapper,
  StyledDrawer,
  StyledDrawerBody,
} from './NodeEditor.styled';
import { getEditorWidth } from './nodeEditorUtils';
import { OverlayContainer } from './ResizableOverlay';
import { getNodeEditorHasChanges } from './utils';

type NodeEditorProps = {
  children?: ReactNode;
  dataTest?: string;
  /**
   * Forces loading icon to be shown in node editor.
   * Useful if using `useQuery` instead of the node sagas
   */
  isLoadingIconShown?: boolean;
  /** Removes any padding from the drawer body if true */
  noPadding?: boolean;
  /** Whether the title of the node editor is editable */
  isTitleEditable?: boolean;
  /** Whether a note is enabled via the node editor */
  isNoteEnabled?: boolean;
  /** Callback for when 'Back' button is clicked  */
  onBack?: () => void;
  /** Callback for when 'Continue' button is clicked  */
  onContinue?: () => void;
  /** Callback for when 'update node' is clicked */
  onUpdate?: (isValidationRequired: boolean) => void;
  /** Returns whether node editor has unsaved changes */
  getHasUnsavedChanges?: (
    initialData?: Partial<NodeData>,
    updatedData?: Partial<NodeData>,
  ) => boolean;
  /** initial nodeData */
  initialNodeData?: Partial<NodeData>;
  /** current nodeData */
  updatedNodeData?: Partial<NodeData>;
  /* Editor type for the node editor */
  editorType?: string;
  /** The node being edited */
  node?: Node;
  /** Whether editor is currently updatable */
  canUpdate?: boolean;
  /** Show clone warning banner */
  isCloneWarningBannerShown?: boolean;
  /** Is clonedFromNodeData loading */
  isClonedFromNodeDataLoading?: boolean;
  /** Should fetch field mappings used for filters */
  shouldFetchMappings?: boolean;
  /** Additional buttons for the footer */
  footerButtons?: ReactNode;
  /** Whether the footer is shown */
  isFooterShown?: boolean;
};

export const NODE_EDITOR_TEST_ID = 'node-editor';
export const UPDATE_BTN_TEST_ID = 'node-editor-update-btn';
export const NOTE_BUTTON = 'note-button';
export const PUBLISHED_NOTE = 'published-note';
export const DRAFT_NOTE = 'draft-note';

export const NODE_EDITOR_TYPE = {
  CAMPAIGN: 'CAMPAIGN',
  OTHER: 'OTHER',
};

export const NodeEditor = ({
  children,
  canUpdate = true,
  dataTest,
  editorType = NODE_EDITOR_TYPE.OTHER,
  footerButtons,
  getHasUnsavedChanges = getNodeEditorHasChanges,
  initialNodeData,
  isCloneWarningBannerShown = false,
  isClonedFromNodeDataLoading = false,
  isFooterShown = true,
  isLoadingIconShown = false,
  isNoteEnabled = true,
  isTitleEditable = true,
  noPadding = false,
  node,
  onBack,
  onContinue,
  onUpdate = noop,
  shouldFetchMappings = false,
  updatedNodeData,
  ...props
}: NodeEditorProps) => {
  const dispatch = useDispatch();
  const { completeIssue, setIsOpen: setIsJourneyReviewOpen } = useJourneyReviewContext();

  /** Used to determine the node editor type */
  const isSendNodeEditor = editorType === NODE_EDITOR_TYPE.CAMPAIGN;

  /** Other selectors */
  const isJourneyReviewPanelEnabled = useSelector(getIsJourneyReviewPanelEnabled);
  const isJourneyFieldMappingsEnabled = useSelector(getIsJourneyFieldMappingsEnabled);

  const canDraftMessaging = useSelector((state: any) =>
    selectDoesUserHaveRbacPermission(state)(PROJECT_PERMISSIONS.DraftMessaging),
  );
  const { isPublished: isWorkflowPublished, isDraft: isWorkflowDraft } =
    useSelector(getWorkflowType);
  const areMappingsPopulated = useSelector(getAreMappingsPopulated);

  /* Details derived from node data */
  const { type: nodeType, title, description, id: openedNodeId, nodeData } = node || ({} as Node);
  const baseConfig = getBaseNodeConfig(nodeType as string) || ({} as NodeConfig);
  const { accentColor, icon: Icon } = baseConfig;
  const size = getEditorWidth(nodeType);
  const isInvalidNode = useSelector((state: WorkflowsRootState) =>
    getIsClonedNodeInvalid(state, openedNodeId),
  );

  /* Used for notes */
  const hasNote = description != null; // allows for any string, including empty
  const showNote = isNoteEnabled && hasNote;
  const allowNoteEdit = isNoteEnabled && !hasNote && isWorkflowDraft;

  /* Loading state */
  const isNodeOpening = useSelector(getNodeIsOpening);
  const isNodeEditorAsyncLoading = useSelector(getIsNodeEditorAsyncLoading);
  const showLoader = isNodeOpening || isLoadingIconShown;
  const isJourneyLoading = isNodeEditorAsyncLoading || isClonedFromNodeDataLoading;

  /* Read only state
   *  Note: in send node editors, we are using the campaign context value
   */
  const isNodeEditorReadOnly = useSelector(getIsNodeEditorReadOnly);
  const { isReadOnly: isCampaignReadOnly } = useCampaign();
  const isTileEditorReadOnly = isSendNodeEditor ? isCampaignReadOnly : isNodeEditorReadOnly;

  /* Change handlers */
  const handleCloseEditor = useCallback(() => {
    dispatch(closeNode());
    dispatch(deselectNode(openedNodeId));
  }, [dispatch, openedNodeId]);

  const handleNodeUpdate = useCallback(() => {
    if (isJourneyReviewPanelEnabled) {
      completeIssue(openedNodeId);
      setIsJourneyReviewOpen(false);
    } else {
      dispatch(removeNodeError());
    }

    if (isInvalidNode && openedNodeId) dispatch(removeClonedJourneyErrorNode(openedNodeId));
    if (onUpdate) {
      onUpdate(false);
    } else {
      dispatch(updateNode({ nodeData }));
      handleCloseEditor();
    }
  }, [dispatch, handleCloseEditor, isInvalidNode, nodeData, onUpdate, openedNodeId]);

  const openHasUnsavedChangesModal = useCallback(
    () =>
      dispatch(
        openModal(NODE_HAS_UNSAVED_CHANGES_MODAL, {
          isSendNodeEditor,
          onCloseEditor: handleCloseEditor,
          onUpdateEditor: handleNodeUpdate,
          canUpdate,
          nodeData,
        }),
      ),
    [dispatch, handleCloseEditor, handleNodeUpdate, canUpdate, isSendNodeEditor, nodeData],
  );

  const checkForUnsavedChanges = useCallback(() => {
    const hasUnsavedChanges = getHasUnsavedChanges(initialNodeData, updatedNodeData);

    if (hasUnsavedChanges && !isTileEditorReadOnly) {
      openHasUnsavedChangesModal();
      return;
    }

    handleCloseEditor();
  }, [
    getHasUnsavedChanges,
    handleCloseEditor,
    initialNodeData,
    isTileEditorReadOnly,
    openHasUnsavedChangesModal,
    updatedNodeData,
  ]);

  const updateNodeTitle = useCallback(
    (editableNodeTitle: any) => {
      if (editableNodeTitle) dispatch(updateNode({ title: editableNodeTitle }));
    },
    [dispatch],
  );

  const updateEditorNote = useCallback(
    (note: any) => dispatch(updateNode({ description: note })),
    [dispatch],
  );

  /* Component DOM reference and focus trap
   *  This allows us to react to clicks on `NodeEditorWrapper` but not `StyledDrawer`
   */
  const mainDrawerRef = useRef<HTMLDivElement>(null);
  const nodeEditorWrapperRef = useRef<HTMLDivElement>(null);
  const intrinsicClick = useIntrinsicClick(nodeEditorWrapperRef);
  const escPressed = useKeyPress('Escape');

  // only initialize focus trap if editor is not currently in loading state
  // and has actual keyboard-interactable elements displayed
  useFocusTrap(handleCloseEditor, mainDrawerRef, showLoader);

  useEffect(() => {
    if (escPressed || intrinsicClick) {
      checkForUnsavedChanges();
    }
  }, [escPressed, intrinsicClick]);

  useEffect(() => {
    if (
      !areMappingsPopulated &&
      (isWorkflowDraft || shouldFetchMappings) &&
      !isJourneyFieldMappingsEnabled
    ) {
      dispatch(fetchEventFieldMappingsRequest());
    }
  }, [
    areMappingsPopulated,
    isWorkflowDraft,
    shouldFetchMappings,
    dispatch,
    isJourneyFieldMappingsEnabled,
  ]);

  return (
    <NodeEditorWrapper
      data-test={dataTest || NODE_EDITOR_TEST_ID}
      {...props}
      ref={nodeEditorWrapperRef}
    >
      <StyledDrawer size={size}>
        {showLoader ? (
          <Spinner />
        ) : (
          <MainDrawer ref={mainDrawerRef}>
            <DrawerHeader>
              <HeaderNodeDetails>
                <IconContainer color={isTileEditorReadOnly ? colors.grey5 : accentColor}>
                  {Icon && (
                    <Icon color={isTileEditorReadOnly ? colors.textPrimary : colors.white} />
                  )}
                </IconContainer>
                <EditableTileHeader
                  title={title || node?.defaultTitle || 'Editor'}
                  onInputSave={updateNodeTitle}
                  maxLength="50"
                  isEditable={isWorkflowDraft && isTitleEditable && canDraftMessaging}
                />
              </HeaderNodeDetails>

              {allowNoteEdit && (
                <NoteButton data-test={NOTE_BUTTON} onClick={() => updateEditorNote('')} />
              )}

              <GhostButton onClick={checkForUnsavedChanges}>
                <Close24 />
              </GhostButton>
            </DrawerHeader>
            <StyledDrawerBody noPadding={noPadding}>
              <OverlayContainer
                hasNote={hasNote}
                showOverlay={isJourneyLoading}
                showSpinner={isJourneyLoading}
              >
                {showNote && (
                  <EditorNote
                    data-test={isWorkflowPublished ? PUBLISHED_NOTE : DRAFT_NOTE}
                    noteText={description}
                    updateNoteText={updateEditorNote}
                    deleteNote={() => updateEditorNote(null)}
                    isReadOnly={isNodeEditorReadOnly}
                  />
                )}
                {isCloneWarningBannerShown && <CloneErrorBanner />}
                {children}
              </OverlayContainer>
            </StyledDrawerBody>
            {isFooterShown && (
              <NodeEditorFooter
                areActionsDisabled={!canUpdate || !canDraftMessaging || isJourneyLoading}
                footerButtons={footerButtons}
                isSendNodeEditor={isSendNodeEditor}
                isWorkflowPublished={isWorkflowPublished}
                nodeData={nodeData}
                onBack={onBack}
                onCloseEditor={handleCloseEditor}
                onContinue={onContinue}
                onNodeUpdate={handleNodeUpdate}
              />
            )}
          </MainDrawer>
        )}
      </StyledDrawer>
    </NodeEditorWrapper>
  );
};
